import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

import Pagination from '@/components/commons/pagination';
import styles from '@/components/commons/table/Table.module.css';
import { PAGE_SIZE } from '@/services/sorting';

import LockedCell from './LockedCell';

function OrderableTable({
    columns,
    data,
    paginationState = null,
    editFunc,
    externalStyles = '',
}) {
    const [isDragging, setIsDragging] = useState(false);
    const [page, setPage] = useState(paginationState ? paginationState.initialPage : 0);
    const totalPages = Math.ceil(data.length / PAGE_SIZE);

    const onBeforeDragStart = () => setIsDragging(true);

    const onDragEnd = (result) => {
        setIsDragging(false);

        if (!result.destination) {
            return;
        }

        if (result.source.index === result.destination.index) {
            return;
        }

        const element = data.filter((ele) => result.source.index + 1 === ele.position);

        editFunc(parseInt(element[0].id, 10), result.destination.index + 1);
    };

    const getDisplayedData = (idx, rows) => {
        const start = idx * PAGE_SIZE;
        return rows.slice(start, start + PAGE_SIZE);
    };

    const renderTableBody = () => (
        <Droppable droppableId="droppable">
            {(provided) => (
                <tbody
                    className=""
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                >
                    {getDisplayedData(page, data).map((row, index) => (
                        <Draggable key={row.id} draggableId={row.id} index={index}>
                            {(draggableProvided) => (
                                <tr
                                    key={row.id}
                                    ref={draggableProvided.innerRef}
                                    {...draggableProvided.draggableProps}
                                    {...draggableProvided.dragHandleProps}
                                    className="bg-white border border-lightGray rounded-md grid grid-cols-[0.2fr,2.5fr,1fr,0.5fr,0.5fr] grid-rows-1 gap-0 items-center"
                                >
                                    {columns.map((column) => {
                                        const key = `${row.id}-${column.id}`;
                                        return (
                                            <LockedCell key={`${key}-td`} isDragOccurring={isDragging}>
                                                {column.render(key, row)}
                                            </LockedCell>
                                        );
                                    })}
                                </tr>
                            )}
                        </Draggable>
                    ))}
                    {provided.placeholder}
                </tbody>
            )}
        </Droppable>
    );

    return (
        <DragDropContext onDragEnd={onDragEnd} onBeforeDragStart={onBeforeDragStart}>
            <div className={styles.componentWrapper}>
                <div className="overflow-x-auto">
                    <table className={`w-full min-w-[750px] overflow-hidden ${externalStyles} `}>
                        {renderTableBody()}
                    </table>
                </div>
                {paginationState && totalPages > 1
                    && (
                        <div className="flex justify-center py-6">
                            <Pagination
                                initialPage={paginationState.initialPage}
                                totalPages={totalPages}
                                windowSize={paginationState.windowSize}
                                onPageChange={setPage}
                            />
                        </div>
                    )}
            </div>
        </DragDropContext>
    );
}

export default OrderableTable;
